// The next OGS render path: load the bundle, then mount the shell its KIND needs.
// Notes: ~/comments/aos-threejs-poc/NextOGSAvatar_kind_dispatch_no_fallthrough.md

import { useEffect, useMemo, useRef } from 'react'
import { createOgsLogger } from '../../ogs/pipeline/ogsLog.ts'
import { useOgsBoot } from './useOgsBoot.js'
import PartRigAvatar from './PartRigAvatar.jsx'
import LiveRigAvatar from './LiveRigAvatar.jsx'

/** Kinds this path can render today. A bundle marked `next` for anything else RAISES. */
export const SUPPORTED_KINDS = ['part-rig', 'live-rig']

/** Named cause, named fix — never a silent fall back to the path the bundle opted out of. */
export function unsupportedKindMessage(kind) {
  return `This bundle asks for the next OGS render path (scene.json render_settings), which cannot `
    + `render a '${kind}' bundle yet. Remove render_settings from the bundle's scene.json to render `
    + `it on the current path.`
}

export default function NextOGSAvatar({ settings, slug, animationBuffer, onReady, onError, onRenderBlocked }) {
  const log = useMemo(
    () => createOgsLogger({ level: settings?.logging, prefix: 'ogs:next' }),
    [settings?.logging],
  )
  const boot = useOgsBoot({ slug, log })
  // Reported once per character: App's onError is an inline arrow, so depending on it
  // would re-fire this on every parent render.
  const reported = useRef(null)

  useEffect(() => {
    if (boot.state === 'error') {
      const key = `error:${slug}`
      if (reported.current === key) return
      reported.current = key
      onError?.(new Error(boot.error ?? 'the OGS bundle could not be loaded'))
      return
    }
    if (boot.state !== 'ready' || SUPPORTED_KINDS.includes(boot.kind)) return
    const key = `kind:${slug}:${boot.kind}`
    if (reported.current === key) return
    reported.current = key
    const message = unsupportedKindMessage(boot.kind)
    log.error(`'${slug ?? 'unknown'}': ${message}`)
    onError?.(new Error(message))
  }, [boot.state, boot.kind, boot.error, slug, log, onError])

  if (boot.state !== 'ready' || !SUPPORTED_KINDS.includes(boot.kind)) return null
  const Shell = boot.kind === 'live-rig' ? LiveRigAvatar : PartRigAvatar
  return (
    <Shell
      boot={boot}
      slug={slug}
      log={log}
      animationBuffer={animationBuffer}
      onReady={onReady}
      onError={onError}
      onRenderBlocked={onRenderBlocked}
    />
  )
}
